'use client';

import { matchesPublicCategoryProductsSeed, type PublicCategoryProductsSeed } from '@eliadmin/core/src/components/categories/public-category-products-seed';
import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { ProductCard } from '../product/product-card';
import { CategoryFilterSheet } from '@eliadmin/core/src/components/categories/category-filter-sheet';
import CategorySort from '@eliadmin/core/src/components/categories/category-sort';
import { ProductSkeletonCard } from '../product/product-skeleton-container';
import { Button } from '../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type CategoryProps = {
  initialProducts?: PublicCategoryProductsSeed;
  className?: {
    grid_wrapper?: string;
    product?: string;
  };
  category?: Category;
  onProductTotalChange?: (total: number) => void;
  paginationProps?: { scrollToElementAfterPageChange?: string };
};

export function CategoryProducts(props: CategoryProps) {
  const isInitialized = useCustomerStore(state => state.isInitialized);

  if (!isInitialized && !props.initialProducts) return <CategoryProductsSkeleton />;

  return <CategoryProductsContent {...props} />;
}

function CategoryProductsContent(props: CategoryProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const filters = useCategoryStore(s => s.filter);
  const searchParams = useSearchParams();

  const currentPage = searchParams.get('page');

  const generalLayout = useGlobalStore(s => s.generalLayout);

  const queryWithoutPage = buildProductsQuery({
    categories: [props?.category?.id || undefined],
    manufacturers: filters.manufacturers ?? [],
    results: generalLayout?.pagination?.paginationProducts || 12,
    order: filters.sort.order,
    orderBy: filters.sort.orderBy,
    attributes: filters.attributes,
    price_from: filters.price_min,
    price_to: filters.price_max,
    availabilities: filters.availabilities,
    suppliers: filters.suppliers,
  });

  const productRequestUri = buildProductsRequest({
    ...queryWithoutPage,
    page: currentPage ? parseInt(currentPage) : 1,
  }).uri;

  const productsQuery = useSuspenseInfiniteQuery({
    initialData: matchesPublicCategoryProductsSeed(props.initialProducts, {
      locale, requestUri: productRequestUri, authToken, page: Number(currentPage ?? 1),
    }) ? { pages: [props.initialProducts!.data], pageParams: [1] } : undefined,
    initialDataUpdatedAt: props.initialProducts?.updatedAt,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000,
    queryKey: [CacheKey.PRODUCTS, 'infinite', currentPage, productRequestUri, authToken, locale],
    queryFn: ({ pageParam }) => {
      return fetchProducts({
        query: {
          ...queryWithoutPage,
          page: pageParam,
        },
        locale,
        authToken,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage ? parseInt(currentPage) : 1,
  });

  const isLoading = productsQuery.isLoading;

  const lastPage = productsQuery.data?.pages[productsQuery.data?.pages.length - 1];
  const flatList = productsQuery.data?.pages.flatMap(page => page.data) ?? [];

  useEffect(() => {
    props.onProductTotalChange?.(lastPage?.meta?.total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage?.meta?.total]);

  const { t } = useTranslations();

  return (
    <section
      className={cn(
        'custom-section',
        'gap-4 sm:gap-6 lg:gap-8',
        'ea-section-small__top',
        'ea-section-big__bottom',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'hidden flex-col items-center justify-center gap-4 sm:flex sm:flex-row sm:justify-between',
        )}
      >
        <div className="w-full sm:max-w-[300px]">
          <CategoryFilterSheet />
        </div>
        <p className="whitespace-nowrap text-center text-[#666666]">
          {productsQuery.data.pages[0].meta.total} {t('text173')}
        </p>
        <CategorySort />
      </div>
      <div className="flex w-full flex-col items-center justify-center gap-4 sm:hidden">
        <div className="w-full sm:max-w-[300px]">
          <CategoryFilterSheet />
        </div>
        <CategorySort />
        <p className="whitespace-nowrap text-center text-[#666666]">
          {productsQuery.data.pages[0].meta.total} {t('text173')}
        </p>
      </div>
      <div
        className={cn(
          'custom-container',
          'grid grid-cols-1 gap-x-4 gap-y-8 sm:mt-0 sm:grid-cols-2 lg:grid-cols-3 xl:gap-x-6',
        )}
      >
        {isLoading
          ? [...Array(8)].map((_, index) => <ProductSkeletonCard key={index} />)
          : flatList.map(product => <ProductCard product={product} key={product.slug} />)}
      </div>
      {productsQuery.hasNextPage && (
        <Button
          onClick={() => productsQuery.fetchNextPage()}
          disabled={productsQuery.isFetchingNextPage}
          className="mx-auto w-full sm:w-fit"
        >
          {t('text494')}
        </Button>
      )}
    </section>
  );
}

type CategoryProductsSkeletonProps = {
  className?: {
    items_wrapper?: string;
    item?: string;
  };
  numberOfSkeletons?: number;
};

export function CategoryProductsSkeleton({
  className,
  numberOfSkeletons,
}: CategoryProductsSkeletonProps) {
  const array = Array.from({ length: numberOfSkeletons || 6 }, (_, index) => index);

  return (
    <div
      className={cn(
        'custom-container grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:gap-x-6',
        className?.items_wrapper,
      )}
    >
      {array.map(index => (
        <div key={index} className={className?.item}>
          <ProductSkeletonCard />
        </div>
      ))}
    </div>
  );
}
